// =============================================================================
// features/documents/lib/downscaleImage.ts — Downscale + limpeza de metadado
// de imagem no browser antes do upload (F46-S08,
// planejamento-documentos-anexos.md §4).
//
// Duas decisões independentes (LGPD doc 17 §8.3 — correção pós-review,
// F46-S08 PR #682):
//   1. Redimensionar? Só quando o lado maior > 1600px. Existe pra poupar
//      banda de agente de campo em 3G.
//   2. Limpar metadado (EXIF)? SEMPRE, incondicionalmente, para qualquer
//      raster que passa por aqui — independente de precisar ou não de
//      redimensionar. Sem isso, uma foto de documento que já chega com
//      <=1600px (câmera com resolução reduzida, screenshot, imagem já
//      comprimida por outro app) preserva o EXIF completo, incluindo
//      latitude/longitude de onde a captura foi feita — PII que não está
//      no DPIA e ninguém pediu. `canvas.toBlob` nunca serializa EXIF (a
//      especificação HTML não propaga metadata do bitmap fonte para o
//      blob gerado), então redesenhar em canvas — mesmo sem redimensionar
//      — já é suficiente pra limpar.
//
// Qualidade de reencode acompanha a decisão de resize, não a de limpeza:
//   - Redimensionando: JPEG_QUALITY (0.82) — a redução de peso é o objetivo,
//     perda perceptual é aceitável.
//   - Só limpando (sem resize): STRIP_ONLY_QUALITY (0.95) — evita degradar
//     imagem que não precisava ser tocada; o reencode aqui existe só pra
//     remover metadado, não pra economizar banda.
//
// Preserva orientação EXIF antes de descartar o resto do metadado
// (createImageBitmap({ imageOrientation: 'from-image' }) já aplica a
// rotação — o buffer decodificado nasce "em pé").
//
// Substituição do arquivo original:
//   - Path de resize: só troca se o resultado ficar MENOR (reencode de
//     imagem já pequena/comprimida pode crescer; nesse caso mantém o
//     original teria voltado a expor EXIF, então esse atalho só existe
//     no path de resize, nunca no de limpeza-só).
//   - Path de limpeza-só (sem resize): troca SEMPRE que o reencode tiver
//     sucesso — remover geolocalização não é negociável, mesmo que o
//     blob final fique alguns bytes maior que o original.
//
// Qualquer falha de decodificação (formato não suportado pelo browser,
// arquivo corrompido) ou de acesso ao canvas 2D devolve o arquivo original
// — nunca bloqueia o upload (mesmo que isso, no caso raríssimo de canvas
// indisponível, deixe o EXIF intacto).
// =============================================================================

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;
const STRIP_ONLY_QUALITY = 0.95;

/**
 * Redesenha a imagem em canvas para (a) reduzir o lado maior a
 * `MAX_DIMENSION` quando necessário e (b) sempre remover metadado (EXIF,
 * incluindo GPS). Devolve o `File` original apenas quando:
 *   - o mime não é imagem (inclui PDF);
 *   - é SVG (vetor — não passa por canvas/EXIF);
 *   - a decodificação falha por qualquer motivo;
 *   - o canvas 2D não está disponível;
 *   - o reencode falhou (`toBlob` devolveu `null`);
 *   - a imagem foi redimensionada mas o reencode não ficou menor que o
 *     original (única situação em que o atalho de "manter original" ainda
 *     é permitido — porque nesse caso não houve limpeza de metadado a
 *     preservar).
 */
export async function downscaleImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) {
    return file;
  }
  // SVG é vetor — downscale via canvas não se aplica (rasterizaria sem ganho
  // de fidelidade e perderia a natureza vetorial). SVG também não carrega
  // EXIF de bitmap.
  if (file.type === 'image/svg+xml') {
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }

  try {
    const { width, height } = bitmap;
    const largerSide = Math.max(width, height);

    // Decisão 1 (independente): redimensionar ou manter as dimensões
    // originais — a limpeza de metadado acontece nos dois casos.
    const shouldResize = largerSide > MAX_DIMENSION;
    const scale = shouldResize ? MAX_DIMENSION / largerSide : 1;
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return file;
    }

    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);

    // Decisão 2 (independente, incondicional): sempre reencoda pra limpar
    // metadado. Qualidade varia com a decisão 1.
    const quality = shouldResize ? JPEG_QUALITY : STRIP_ONLY_QUALITY;
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
    });

    if (!blob) {
      return file;
    }

    // O atalho de "manter original por tamanho" só existe no path de
    // resize (comportamento pré-existente: imagem grande cujo reencode não
    // comprimiu o suficiente). No path de limpeza-só (`!shouldResize`) não
    // há atalho por tamanho — a troca é sempre aplicada, porque o único
    // motivo de passar por aqui é remover metadado, e voltar ao arquivo
    // original reintroduziria o EXIF (incluindo GPS) que acabamos de
    // limpar.
    if (shouldResize && blob.size >= file.size) {
      return file;
    }

    const newName = file.name.replace(/\.[^./]+$/, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } finally {
    bitmap.close?.();
  }
}
